import { Analysis } from '../services/storyAnalysisApi/types';
import type { AuthorInterface } from './author.interface';
import { FeedMetadata, PostStats, ViewerInteractions } from './feed.interface';
import type { FollowedByInterface } from './followedBy.interface';
import type { NextImage } from './nextImage.interface';
import type { PostAnalyticsInterface } from './postAnalytics.interface';
import type { PostMetaInterface } from './postMeta.interface';
import type { PostReputationInterface } from './reputation.interface';
import type { TextBlock, TextBlocksRevision } from './textEditor.interface';
import type { VideoTag } from './videoTag.interface';

export interface PostMedia {
  photo: NextImage;
  video: VideoTag;
}

export interface NewPostInput {
  textBlocks?: TextBlock[];
  username: string | undefined;
  status: DocumentStatus;
  categories?: string[];
  userId?: string;
  article?: boolean;
  id?: string;
  parentPostId: string | undefined;
}

export interface NewDraftInput {
  textBlocks?: TextBlock[];
  username: string | undefined;
  userId?: string;
  article?: boolean;
}

export interface DraftInterfaceState {
  draftStore: DraftInterfaceSliceState;
}

export interface PostReplyDraftsInterfaceState {
  postReplyDraftsStore: PostReplyDraftsInterfaceSliceState;
}

export interface PostReplyDraftInterfaceState {
  postReplyDraftStore: PostReplyDraftInterfaceSliceState;
}

export type AutoCompleteCategories = { label: string };

export type ChipData = {
  key: number;
  label: string;
};

export interface NewDraftCategoriesInput {
  categories: string[];
  id: string;
}

export type PostReplyDraftsInterfaceSliceState = {
  postReplyDrafts: PostDraftInterface[];
  postReplyDraft: PostDraftInterface;
  textBlocks: TextBlock[];
  draft?: PostDraftInterface;
  categories?: string[];
  createDraft?: false;
  editDraft?: false;
  isEditingDraft?: false;
  isEditingPostReplyDraft?: false;
  saveDraftStart?: false;
  saveDraftSuccess?: false;
  refetchPostReplyDrafts?: false;
  publishDraft?: false;
  updatedPostDraft?: {};
  isAddingPostReplyDraft?: boolean;
  savedImageKey?: string[];
};

export interface DraftInterfaceSliceState extends DraftInterfaceSliceStateBase {
  draft: PostDraftInterface;
  drafts: PostDraftInterface[];
  isEditingDraft?: boolean;
  refreshDraft?: boolean;
  publishDraftInit: boolean;
  publishDraftSuccess: boolean;
  // TRIL-116: primary topic of the most recently published post — drives the
  // global <PostPublishPrompt /> mount in App.tsx. Null when no publish has
  // occurred this session or after the prompt has been dismissed/CTA-ed.
  lastPublishedTopic: string | null;
}

export interface PostReplyDraftInterfaceSliceState
  extends DraftInterfaceSliceStateBase {
  postReplyDraft: PostDraftInterface;
  postReplyDrafts: PostDraftInterface[];
  isEditingPostReplyDraft?: boolean;
  refreshPostReplyDraft?: boolean;
  publishPostReplyInit: boolean;
  publishPostReplySuccess: boolean;
}

export type DraftInterfaceSliceStateBase = {
  textBlocks: TextBlock[];
  categories?: string[];
  addDraft: boolean;
  createDraft?: boolean;
  editDraft?: boolean;
  saveDraftStart?: boolean;
  saveDraftSuccess?: boolean;
  publishDraft?: boolean;
  updatedPostDraft?: {};
  isAddingDraft?: boolean;
  isAddingPostReplyAsDraft?: boolean;
  savedImageKey?: string[];
  reAnalyzeStory?: boolean;
  // TRIL-313: raw text of the last-analyzed content; normalized at compare time
  // to suppress re-analysis on cosmetic-only edits.
  lastAnalyzedText?: string | null;
  reAnalyzeReply?: boolean;
  feedPostId: string | undefined;
  deletedDraftId: string;
};

export interface PublishedDraft {
  draft: PostDraftInterface;
}

export type PostId = {
  id: string;
};

export type DraftId = {
  id: string;
};

export interface UserFeedInterface {
  likedPosts: string[];
}

export interface PostReplyInterface extends CoreDocumentInterface {
  // biome-ignore lint/suspicious/noExplicitAny: postId may be a populated doc or an id string (legacy Mongo shape)
  postId: string | Record<any, any>;
  rootPostId: string;
  followedBy: FollowedByInterface;
  createdAt: Date;
  updatedAt: Date;
  author: AuthorInterface;
  postAnalytics: PostAnalyticsInterface;
  reputation?: PostReputationInterface;
  meta?: PostMetaInterface;
  media?: PostMedia;
  viewerInteractions?:
    | {
        viewerInteractionsId: {
          _id: string;
        };
      }
    | ViewerInteractions;
  feedMetadata?: FeedMetadata;
  stats?: PostStats;
  id?: string;
  username?: string;
  isFollowingAuthor?: string;
}

export interface PostsTable extends CoreDocumentInterfaceDDB {
  id: string | Record<any, any>;
  createdAt: Date;
  updatedAt: Date;
  authorId: string;
  username?: string;
  followedBy?: FollowedByInterface;
  author?: AuthorInterface;
  postAnalytics: PostAnalyticsInterface;
  reputation?: PostReputationInterface;
  meta?: PostMetaInterface;
  viewerInteractions?: ViewerInteractions;
  feedMetadata?: FeedMetadata;
  stats?: PostStats;
}

export interface CoreDocumentInterfaceDDB {
  status: 'draft' | 'published' | 'archived' | 'deleted';
  categories: string[];
  textBlocks: TextBlock[];
  article: boolean;
  textBlocksVersions: TextBlocksRevision[];
  username?: string;
  hasStoryAnalysis?: boolean;
}

export interface DraftsTable extends CoreDocumentInterfaceDDB {
  id: string;
  blocks?: TextBlock[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PostDraftInterface extends CoreDocumentInterface {
  id: string;
  blocks?: TextBlock[];
  createdAt?: Date;
  updatedAt?: Date;
  username?: string;
  parentPostId?: string;
}

export enum DocumentStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

export interface CoreDocumentInterface {
  status: DocumentStatus;
  categories: string[];
  textBlocks: TextBlock[];
  article: boolean;
  textBlocksVersions: TextBlocksRevision[];
  hasStoryAnalysis: boolean;
  storyAnalysis: Analysis[];
  PK?: string;
  parentPostId?: string;
}

export interface LogInPostInterface {
  id: string;
  textBlocks: TextBlock[];
  textBlocksVersions: TextBlocksRevision[];
  author: AuthorInterface;
  postAnalytics: PostAnalyticsInterface;
  reputation: PostReputationInterface;
  meta: PostMetaInterface;
  article: boolean;
  createdAt: Date;
  updatedAt: Date;
  media?: PostMedia;
  _doc: Document;
}

export interface PostBodyRevision {
  id: string;
  textBlocks: TextBlock[];
  revisionId?: string;
  date?: string;
  author?: AuthorInterface;
}

export interface UpdatedPayloadI {
  id: string | undefined;
  username: string | undefined;
  textBlocks: TextBlock[];
  status: DocumentStatus;
}
